import type { ARCustomer, LostSale, PartFilters, PartsRow, SalesSummary, SalesTicket, TicketDetail } from './types';

/**
 * Drill-down fetchers — pull live data from the Go proxy /api/sales|parts|ar|salespeople.
 * The proxy forwards to the Progress daemon (paceAPIserv); a long-running query can
 * take a couple of seconds. 60s timeout matches the daemon's worst-case (90-day
 * walks for slow-movers).
 */

const FETCH_TIMEOUT_MS = 60_000;

function isMock(): boolean {
  return import.meta.env.VITE_USE_MOCKS === '1';
}

async function getJSON<T>(url: string): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
    if (res.status === 503) {
      throw new Error('paceAPI daemon offline (503) — drill-downs temporarily unavailable.');
    }
    if (res.status === 502) {
      throw new Error('paceAPI daemon error (502) — request failed at the daemon.');
    }
    if (!res.ok) {
      throw new Error(`${url} failed: ${res.status}`);
    }
    return (await res.json()) as T;
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error(`${url} timed out after ${FETCH_TIMEOUT_MS}ms`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

export interface SalesQuery {
  /** YYYY-MM-DD; defaults to 30 days ago at the daemon. */
  from?: string;
  /** YYYY-MM-DD; defaults to today at the daemon. */
  to?: string;
  sid?: number;
  /** slid is CHARACTER on the wire; pass the salesperson's slid string. */
  slid?: string;
  type_filter?: 'all' | 'cash' | 'credit';
  paid_filter?: 'all' | 'unpaid' | 'paid';
}

export async function fetchSales(q: SalesQuery): Promise<SalesTicket[]> {
  if (isMock()) {
    return [];
  }
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  return getJSON<SalesTicket[]>(`/api/sales?${params.toString()}`);
}

/** Same window/store/salesperson/type filters as SalesQuery — no paid_filter. */
export type SalesSummaryQuery = Omit<SalesQuery, 'paid_filter'>;

const EMPTY_SUMMARY: SalesSummary = {
  totals: { tickets: 0, sales_mxn: 0, cash_mxn: 0, credit_mxn: 0 },
  by_type: [], by_line: [], by_slid: [],
};

/**
 * Aggregate-only companion to fetchSales. The daemon answers it with one flat
 * scan (no per-ticket emit, no artrx lookup, no 5000-row cap), so on a wide
 * window it returns long before the raw ticket list — the Sales page paints
 * KPIs + breakdown charts from it while the detail is still loading.
 */
export async function fetchSalesSummary(q: SalesSummaryQuery): Promise<SalesSummary> {
  if (isMock()) {
    return EMPTY_SUMMARY;
  }
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  return getJSON<SalesSummary>(`/api/sales-summary?${params.toString()}`);
}

export interface PartsQuery {
  search?: string;
  kind?: 'all' | 'low' | 'slow';
  /** Default 200 at the daemon. */
  limit?: number;
  sid?: number;
  line?: string;
  pcode?: string;
  class?: string;
  /** partf.codes[1..3] match (v2.57). */
  code?: string;
}

export async function fetchParts(q: PartsQuery): Promise<PartsRow[]> {
  if (isMock()) {
    return [];
  }
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  return getJSON<PartsRow[]>(`/api/parts?${params.toString()}`);
}

export interface PartFiltersQuery { sid?: number; }

export async function fetchPartFilters(q: PartFiltersQuery = {}): Promise<PartFilters> {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  return getJSON<PartFilters>(`/api/part-filters?${params.toString()}`);
}

export interface ARQuery {
  sort?: 'total' | 'aging90' | 'name';
  dir?: 'asc' | 'desc';
  /** Default 500 at the daemon. */
  limit?: number;
}

export async function fetchAR(q: ARQuery = {}): Promise<ARCustomer[]> {
  if (isMock()) {
    return [];
  }
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  return getJSON<ARCustomer[]>(`/api/ar?${params.toString()}`);
}

export async function fetchTicket(sid: number, tnum: number): Promise<TicketDetail> {
  return getJSON<TicketDetail>(`/api/ticket?sid=${sid}&tnum=${tnum}`);
}

export interface LostSalesQuery {
  from?: string;
  to?: string;
  sid?: number;
  slid?: string;
}

export async function fetchLostSales(q: LostSalesQuery): Promise<LostSale[]> {
  if (isMock()) return [];
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  return getJSON<LostSale[]>(`/api/lost-sales?${params.toString()}`);
}
